import React from 'react';
import { Check, AlertCircle } from 'lucide-react';
import { Box, Typography, Button } from '@mui/material';

export type StepStatus = 'completed' | 'current' | 'error' | 'upcoming';

export interface Step {
  id: string;
  label: string;
  description: string;
  status: StepStatus;
  icon?: React.ReactNode;
}

interface StepIndicatorProps {
  steps: Step[];
  onStepClick?: (stepId: string) => void;
}

export function StepIndicator({ steps, onStepClick }: StepIndicatorProps) {
  return (
    <Box
      sx={{
        width: '93%',
        backgroundColor: 'transparent', // Match dialog content background
        px: 4,
        py: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          
          return (
            <Box key={step.id} sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, position: 'relative' }}>
              {/* Step Content */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                {/* Icon Circle */}
                <Button
                  onClick={() => onStepClick?.(step.id)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 10,
                    cursor: 'pointer',
                    minWidth: 'auto',
                    p: 0,
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                    '&:focus': {
                      outline: 'none',
                      boxShadow: step.status === 'completed' ? '0 0 0 2px #10b981' :
                                  step.status === 'current' ? '0 0 0 2px #3b82f6' :
                                  step.status === 'error' ? '0 0 0 2px #ef4444' :
                                  '0 0 0 2px #64748b',
                    },
                    backgroundColor: step.status === 'completed' ? '#10b981' : // green-500
                                    step.status === 'current' ? '#3b82f6' : // blue-500
                                    step.status === 'error' ? '#ef4444' : // red-500
                                    '#475569', // slate-600
                    color: 'white',
                    boxShadow: step.status === 'current' ? '0 0 0 4px rgba(59, 130, 246, 0.3)' :
                              step.status === 'error' ? '0 0 0 4px rgba(239, 68, 68, 0.3)' : 'none',
                  }}
                >
                  {step.status === 'completed' ? (
                    <Check size={24} color="white" />
                  ) : step.status === 'error' ? (
                    <AlertCircle size={24} color="white" />
                  ) : (
                    <Box sx={{ color: 'white', fontSize: 16, fontWeight: 'bold' }}>
                      {step.icon || index + 1}
                    </Box>
                  )}
                </Button>

                {/* Label and Description */}
                <Box sx={{ mt: 1.5, textAlign: 'center', maxWidth: 180 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{
                      transition: 'color 0.3s ease',
                      color: step.status === 'completed' ? '#4ade80' : // green-400
                             step.status === 'current' ? '#60a5fa' : // blue-400
                             step.status === 'error' ? '#f87171' : // red-400
                             '#94a3b8', // slate-400
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                    {step.description}
                  </Typography>
                  
                  {/* Error Badge */}
                  {step.status === 'error' && (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        color: '#f87171',
                      }}
                    >
                      <AlertCircle size={12} />
                      <span>Has validation issues</span>
                    </Box>
                  )}
                  
                  {/* Completed Badge */}
                  {step.status === 'completed' && (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'rgba(16, 185, 129, 0.2)',
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        color: '#4ade80',
                      }}
                    >
                      <Check size={12} />
                      <span>Completed</span>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Connecting Line */}
              {!isLast && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 24,
                    left: '50%',
                    width: '100%',
                    height: 2,
                    zIndex: 0,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: step.status === 'completed' ? '#10b981' : '#475569',
                      transition: 'background-color 0.3s ease',
                    }
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
